import { useEffect, useRef, useState } from 'react';
import { fmtDate } from '@/lib/dates';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Bell, Check, CheckCircle2, ChevronRight, Info, Loader2, X } from 'lucide-react';
import { me, type EisfTaskRow } from '@/api/client';
import { encryptId } from '@/lib/encryptId';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Sprint-18 (eISF spec §edge case — Expired Delegation Log auto-task) —
 * inbox bell in the AppShell header.
 *
 * Polls `/me/tasks?status=open` every 60s. Dropdown lists open tasks
 * for the caller (by user_id OR role match). Each task has Resolve +
 * Dismiss actions. Badge count + colour drives from the highest
 * severity in the list.
 */

const POLL_INTERVAL_MS = 60_000;

// Uses the app's own critical/high/medium/low status tokens (tailwind.config.js)
// instead of ad hoc amber/slate shades. `text` is chosen per background for a
// real WCAG AA contrast ratio (>=4.5:1) rather than a fixed white — amber-ish
// `high` (#D69E2E, ~2.4:1 with white) and blue `medium` (#3B7AC1, ~4.4:1 with
// white — just under the threshold) both need dark text; the darker
// `critical` and mid-gray `low` tones clear 4.5:1 with white.
const SEVERITY_BADGE: Record<EisfTaskRow['severity'], { dot: string; ring: string; text: string }> = {
  critical: { dot: 'bg-critical', ring: 'ring-critical/20', text: 'text-white' },
  high:     { dot: 'bg-high',     ring: 'ring-high/20',     text: 'text-ink-primary' },
  medium:   { dot: 'bg-medium',   ring: 'ring-medium/20',   text: 'text-ink-primary' },
  low:      { dot: 'bg-low',      ring: 'ring-low/20',      text: 'text-white' },
};

export function TasksBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const qc = useQueryClient();

  const tasksQ = useQuery({
    queryKey: ['me', 'tasks', 'open'],
    queryFn: () => me.listTasks('open'),
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 30_000,
  });

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const tasks = tasksQ.data?.tasks ?? [];
  const count = tasks.length;

  const resolve = useMutation({
    mutationFn: (args: { taskId: string; status: 'resolved' | 'dismissed' }) =>
      me.resolveTask(args.taskId, args.status),
    onSuccess: (_, vars) => {
      toast.success(vars.status === 'resolved' ? 'Task resolved.' : 'Task dismissed.');
      qc.invalidateQueries({ queryKey: ['me', 'tasks', 'open'] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed')),
  });

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded text-white/80 hover:text-white hover:bg-white/10"
        title={count === 0 ? 'No open tasks' : `${count} open task${count === 1 ? '' : 's'}`}
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-white text-[10px] font-bold text-accent-600 ring-2 ring-white/40">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[420px] max-h-[70vh] bg-white border border-surface-border rounded-md shadow-2xl overflow-hidden flex flex-col z-50">
          <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between">
            <div className="text-sm font-semibold text-ink-primary">My tasks</div>
            <div className="text-xs text-ink-muted">{count} open</div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {tasksQ.isLoading ? (
              <div className="p-6 flex items-center justify-center gap-2 text-sm text-ink-muted">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            ) : tasksQ.isError ? (
              <div className={`p-4 flex items-start gap-2 text-xs ${errorToneClass(tasksQ.error)}`}>
                {isPermissionError(tasksQ.error) ? <Info className="h-3.5 w-3.5 mt-0.5"/> : <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />}
                {errorText(tasksQ.error, 'Failed to load tasks.')}
              </div>
            ) : count === 0 ? (
              <div className="p-8 text-center text-sm text-ink-muted">
                <CheckCircle2 className="h-6 w-6 mx-auto text-emerald-500 mb-2" />
                You&rsquo;re caught up. No open tasks.
              </div>
            ) : (
              <ul>
                {tasks.map((task) => (
                  <TaskRow
                    key={task.task_id}
                    task={task}
                    onResolve={() => resolve.mutate({ taskId: task.task_id, status: 'resolved' })}
                    onDismiss={() => resolve.mutate({ taskId: task.task_id, status: 'dismissed' })}
                    pending={resolve.isPending}
                  />
                ))}
              </ul>
            )}
          </div>

          <div className="px-4 py-2 border-t border-surface-border text-[11px] text-ink-muted">
            Auto-refreshes every {POLL_INTERVAL_MS / 1000}s. Generated daily by the eISF sweep.
          </div>
        </div>
      )}
    </div>
  );
}

function TaskRow({
  task, onResolve, onDismiss, pending,
}: {
  task: EisfTaskRow;
  onResolve: () => void;
  onDismiss: () => void;
  pending: boolean;
}) {
  const sev = SEVERITY_BADGE[task.severity];
  // Bug found live (2026-09-07): the generic Resolve action here just
  // flips ctms_eisf_tasks.status to 'resolved' — it does NOT run the real
  // approval workflow. For a training-approval-needed task specifically,
  // clicking the checkmark made the task vanish while the training stayed
  // stuck in pending_approval forever, with no certifications generated
  // and nothing left to prompt a real approval. Route these to the actual
  // Approval Needed page instead of offering a shortcut that silently
  // fakes completion.
  const isTrainingApproval = task.kind === 'training-approval-needed';
  // 2026-09-28 — protocol amendment approval + review tasks resolve through
  // the study's Protocol amendments panel (approval needs a Part-11
  // e-signature; review tasks need an outcome + note), never a bare tick.
  const isProtocolAmendment =
    (task.kind === 'protocol-amendment-approval-needed' || task.kind.startsWith('protocol-amendment:')) && !!task.study_id;
  return (
    <li className="border-b border-surface-border/40 last:border-0 px-4 py-3 hover:bg-surface-muted/30">
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 h-2 w-2 rounded-full ${sev.dot} shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-ink-primary leading-snug">{task.message}</div>
          <div className="mt-0.5 text-[11px] text-ink-muted flex items-center gap-1.5">
            <span className="capitalize">{task.severity}</span>
            <span>·</span>
            <span className="font-mono">{task.kind}</span>
            <span>·</span>
            <span>{fmtRelative(task.created_at)}</span>
          </div>
        </div>
        {isTrainingApproval ? (
          <Link
            to="/approvals"
            className="shrink-0 self-center rounded border border-surface-border px-2 py-1 text-[11px] font-medium text-ink-secondary hover:bg-surface-muted/50"
          >
            Review
          </Link>
        ) : isProtocolAmendment ? (
          <Link
            to={`/studies/${encryptId(task.study_id!)}`}
            className="shrink-0 self-center rounded border border-surface-border px-2 py-1 text-[11px] font-medium text-ink-secondary hover:bg-surface-muted/50"
          >
            Open study
          </Link>
        ) : (
          <div className="flex flex-col gap-1 shrink-0">
            <button
              type="button"
              onClick={onResolve}
              disabled={pending}
              className="text-emerald-700 hover:text-emerald-800 p-1 rounded hover:bg-emerald-50 disabled:opacity-40"
              title="Mark resolved (issue is fixed)"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onDismiss}
              disabled={pending}
              className="text-ink-muted hover:text-ink-primary p-1 rounded hover:bg-surface-muted/50 disabled:opacity-40"
              title="Dismiss (hide; daily sweep may re-create if issue persists)"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

function fmtRelative(iso: string): string {
  // Caspio returns datetime strings without a timezone suffix (UTC-naive) —
  // append 'Z' so JS parses them as UTC rather than local time.
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  if (isNaN(d.valueOf())) return iso;
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return fmtDate(d);
}

void ChevronRight; // reserved for future "see all" footer link
